/**
 * <SettlementHistoryWidget> — clinician dashboard tile (Feb 2026).
 *
 * Backs the "paid instantly once a visit is documented" pitch claim
 * by surfacing the last 5 verified settlements. Each row shows the
 * released amount (= the txn's clinician_earnings that was held in
 * escrow), any tax reserved on payout, and the payout status.
 *
 * Uses the canonical CaraLoom terminology — "Verified Settlement" —
 * never "escrow" in user-facing copy (that word is platform-wide
 * prohibited; we say "release" and "settled").
 *
 * Reads: GET /api/clinician/settlement/history
 * Server schema (`visit_settlements` row):
 *   { id, note_id, clinician_id, patient_id, session_id,
 *     released_amount, auto_payout, payout_id, tax_reserved,
 *     created_at }
 */
import React, { useEffect, useState } from 'react';
import axios from '../../lib/api';
import { CheckCircle2, Clock, Zap, ArrowRight, Wallet, AlertTriangle } from 'lucide-react';
import { TileHeader } from './BentoWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const fmtRelative = (iso) => {
  try {
    const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

/**
 * Renders the payout-status pill from `visit_settlements.auto_payout`.
 *   null    → user didn't enable auto-payout. Funds are released and
 *             withdrawable from the clinician's balance.
 *   "paid"  → Stripe instant payout succeeded.
 *   "in_transit" / "pending" → standard ACH or instant in flight.
 *   "skipped" → auto-payout ran but had nothing to pay (e.g. balance was 0).
 *   "error" → Stripe call failed; will be retried by ops.
 */
const StatusBadge = ({ row }) => {
  const auto = row.auto_payout;
  if (auto === 'paid') return (
    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-success/15 text-cn-success inline-flex items-center gap-1">
      <Zap className="w-2.5 h-2.5" /> Paid instantly
    </span>
  );
  if (auto === 'in_transit' || auto === 'pending') return (
    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-primary/10 text-cn-primary inline-flex items-center gap-1">
      <Clock className="w-2.5 h-2.5" /> Payout in transit
    </span>
  );
  if (auto === 'error') return (
    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-accent/15 text-cn-accent inline-flex items-center gap-1">
      <AlertTriangle className="w-2.5 h-2.5" /> Payout retry pending
    </span>
  );
  // null OR "skipped" → ready to withdraw from balance
  return (
    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-primary/10 text-cn-primary inline-flex items-center gap-1">
      <Wallet className="w-2.5 h-2.5" /> Ready to withdraw
    </span>
  );
};

export const SettlementHistoryWidget = ({ getAuthHeader }) => {
  const [loading, setLoading] = useState(true);
  const [settlements, setSettlements] = useState([]);
  const [autoPayout, setAutoPayout] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [hist, prefs] = await Promise.all([
          axios.get(`${API}/clinician/settlement/history`, getAuthHeader?.()),
          axios.get(`${API}/clinician/settlement/settings`, getAuthHeader?.()).catch(() => null),
        ]);
        if (active) {
          setSettlements((hist.data?.settlements || []).slice(0, 5));
          setAutoPayout(!!prefs?.data?.auto_payout);
        }
      } catch (e) {
        if (active) setErr(e?.response?.data?.detail || 'Unable to load settlements.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [getAuthHeader]);

  const totalReleased = settlements.reduce((acc, s) => acc + Number(s.released_amount || 0), 0);

  return (
    <div data-testid="settlement-history-widget">
      <TileHeader
        eyebrow="Payouts"
        title="Verified settlements"
        action={
          <a
            href="/clinician/payouts"
            className="text-sm font-semibold text-cn-primary inline-flex items-center gap-1.5 hover:underline"
            data-testid="settlement-history-widget-all"
          >
            All payouts <ArrowRight className="w-4 h-4" />
          </a>
        }
      />

      {loading && (
        <div className="mt-5 space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 rounded-xl bg-cn-bg/60 border border-cn-border/60 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && err && (
        <p className="text-xs text-cn-text-muted mt-4">{err}</p>
      )}

      {!loading && !err && settlements.length === 0 && (
        <div className="mt-5 p-4 rounded-xl border border-dashed border-cn-border/70 bg-cn-bg/60 text-center">
          <Clock className="w-5 h-5 text-cn-text-muted mx-auto mb-2" />
          <p className="text-sm font-semibold text-cn-text dark:text-white">No settled visits yet</p>
          <p className="text-xs text-cn-text-muted mt-1">
            Document and sign a visit and the payment is released
            {autoPayout ? ' — auto-paid instantly to your bank.' : ' to your balance.'}
          </p>
          {!autoPayout && (
            <a
              href="/clinician/banking"
              className="text-xs font-semibold text-cn-primary inline-flex items-center gap-1 mt-3 hover:underline"
              data-testid="settlement-history-enable-auto"
            >
              <Zap className="w-3 h-3" /> Enable auto instant-pay
            </a>
          )}
        </div>
      )}

      {!loading && settlements.length > 0 && (
        <>
          {/* Recent-total header */}
          <div className="mt-5 px-4 py-3 rounded-xl border border-cn-success/30 bg-cn-success/8 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Recent released</p>
              <p className="text-xl font-display font-extrabold text-cn-text dark:text-white"
                 data-testid="settlement-history-recent-total">{fmtUSD(totalReleased)}</p>
            </div>
            <CheckCircle2 className="w-6 h-6 text-cn-success" />
          </div>

          <ul className="mt-3 space-y-2">
            {settlements.map((s) => (
              <li key={s.id}
                  className="p-3 rounded-xl border border-cn-border/70 bg-white dark:bg-cn-ink-800 hover:border-cn-primary/40 transition-colors"
                  data-testid={`settlement-row-${s.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-sm font-semibold text-cn-text dark:text-white">
                        {fmtUSD(s.released_amount)}
                      </p>
                      <StatusBadge row={s} />
                    </div>
                    <p className="text-[11px] text-cn-text-muted truncate">
                      {fmtRelative(s.created_at)}
                      {Number(s.tax_reserved) > 0 && (
                        <span> · {fmtUSD(s.tax_reserved)} set aside for taxes</span>
                      )}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-cn-text-muted mt-3 italic">
            Funds settle direct clinician-to-family once the visit is signed. CaraLoom is a
            software marketplace, not a money transmitter.
          </p>
        </>
      )}
    </div>
  );
};

export default SettlementHistoryWidget;
